import React, { useState, useEffect } from 'react';
import cx from 'classnames';

import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const styles = require('./styles.pcss');

type CustomField = {
  id: string;
  name: string;
  value?: string;
  type?: string;
  options?: string[];
  isRequired?: boolean;
};

type Props = {
  open: boolean;
  onCancel: () => void;
  submit: (fields: { id: string; name: string; value: string }[]) => void;
  customFieldResponses: CustomField[];
  allCustomFields?: CustomField[];
};

const EditCustomCategoriesBookingPopup: React.FC<Props> = ({
  open,
  onCancel,
  submit,
  customFieldResponses,
  allCustomFields = [],
}) => {
  const [responses, setResponses] = useState<CustomField[]>(customFieldResponses || []);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      setResponses(customFieldResponses || []);
      setErrors({});
      setAddMenuAnchor(null);
    }
  // Reset only on open transition, not on every customFieldResponses change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const getAvailableToAdd = (): CustomField[] => {
    const activeIds = new Set(responses.map((r) => r.id));
    return allCustomFields.filter((f) => !f.isRequired && !activeIds.has(f.id));
  };

  const handleChangeValue = (id: string, value: string) => {
    setResponses((prev) => prev.map((r) => (r.id === id ? { ...r, value } : r)));
    setErrors((prev) => ({ ...prev, [id]: '' }));
  };

  const handleAdd = (id: string) => {
    const field = allCustomFields.find((f) => f.id === id);
    if (!field) return;
    setResponses((prev) => [...prev, { ...field, value: '' }]);
    setAddMenuAnchor(null);
  };

  const handleRemove = (id: string) => {
    setResponses((prev) => prev.filter((r) => r.id !== id));
    setErrors((prev) => ({ ...prev, [id]: '' }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    responses.forEach((f) => {
      if (f.isRequired && !f.value?.trim()) {
        newErrors[f.id] = `${f.name} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    submit(responses.map(({ id, name, value }) => ({ id, name, value: value || '' })));
  };

  const renderField = (field: CustomField) => {
    const error = errors[field.id];
    const label = `${field.name}${field.isRequired ? ' *' : ''}`;
    const isOptional = !field.isRequired;

    const removeButton = isOptional ? (
      <IconButton
        size="small"
        aria-label={`Remove ${field.name}`}
        onClick={() => handleRemove(field.id)}
        style={{ marginLeft: 8, flexShrink: 0, alignSelf: 'center' }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    ) : null;

    if (field.type === 'dropdown' && field.options?.length) {
      return (
        <div
          key={field.id}
          style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 16 }}
        >
          <Autocomplete
            fullWidth
            size="small"
            options={field.options!}
            value={field.value || null}
            onChange={(_e, newValue) => handleChangeValue(field.id, newValue || '')}
            filterOptions={(options, state) => {
              const input = state.inputValue.toLowerCase().trim();
              if (!input) return [...options].sort((a, b) => a.localeCompare(b));

              const exactMatch = options.filter(
                (opt) => opt.toLowerCase() === input
              );
              if (exactMatch.length) return exactMatch;

              const filtered = options.filter((opt) =>
                opt.toLowerCase().includes(input)
              );

              return filtered.sort((a, b) => {
                const aStartsWith = a.toLowerCase().startsWith(input);
                const bStartsWith = b.toLowerCase().startsWith(input);
                if (aStartsWith && !bStartsWith) return -1;
                if (!aStartsWith && bStartsWith) return 1;
                return b.localeCompare(a);
              });
            }}
            noOptionsText="No options"
            renderOption={(props, option, { index }) => (
              <li {...props} key={`${option}-${index}`}>
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant="outlined"
                error={!!error}
                helperText={error || undefined}
              />
            )}
            ListboxProps={{ style: { maxHeight: 250 } }}
            clearOnBlur
            handleHomeEndKeys
            openOnFocus
          />
          {removeButton}
        </div>
      );
    }

    return (
      <div
        key={field.id}
        style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 16 }}
      >
        <TextField
          label={label}
          value={field.value || ''}
          onChange={(e) => handleChangeValue(field.id, e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          error={!!error}
          helperText={error || undefined}
        />
        {removeButton}
      </div>
    );
  };

  const availableToAdd = getAvailableToAdd();
  const requiredFields = responses.filter((f) => f.isRequired);
  const optionalFields = responses.filter((f) => !f.isRequired);
  const totalOptional = allCustomFields.filter((f) => !f.isRequired).length;

  const sectionLabel: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.9px',
    textTransform: 'uppercase',
    color: '#6d717a',
    marginBottom: 12,
    display: 'block',
  };

  return (
    <Dialog
      maxWidth={'md'}
      fullWidth={false}
      className={cx(styles.dialogBox, styles.ccPopup)}
      open={open}
      onClose={onCancel}
    >
      <div className={styles.title}>
        <div className={styles.titleStyle}>Edit Custom Fields</div>
        <IconButton
          color="inherit"
          onClick={onCancel}
          className={cx(styles.closeIconCreditCard)}
          aria-label="Close"
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </div>
      <div className={cx(styles.body, styles.addProgram, styles.categoriesPopup)}>
        {requiredFields.length > 0 && (
          <>
            <Typography style={sectionLabel}>Required Fields</Typography>
            {requiredFields.map((field) => renderField(field))}
            <Divider style={{ margin: '4px 0 20px' }} />
          </>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <Typography style={{ ...sectionLabel, marginBottom: 0 }}>
            Optional Fields
            {totalOptional > 0 && ` (${optionalFields.length} / ${totalOptional})`}
          </Typography>
          {availableToAdd.length > 0 && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => setAddMenuAnchor(e.currentTarget)}
              style={{
                textTransform: 'none',
                fontSize: 13,
                color: '#318afb',
                padding: '2px 8px',
              }}
            >
              Add Field
            </Button>
          )}
        </div>

        {optionalFields.length === 0 ? (
          <Typography
            variant="body2"
            style={{
              color: '#9e9e9e',
              fontStyle: 'italic',
              textAlign: 'center',
              padding: '16px 0 20px',
            }}
          >
            No optional fields added
          </Typography>
        ) : (
          optionalFields.map((field) => renderField(field))
        )}

        <Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={() => setAddMenuAnchor(null)}
        >
          {availableToAdd.map((f) => (
            <MenuItem
              key={f.id}
              onClick={() => handleAdd(f.id)}
            >
              {f.name}
            </MenuItem>
          ))}
        </Menu>

        <div className={cx(styles.popupButtonsContainer, styles.addNewTableRowProgram)}>
          <Button
            size="small"
            className={cx(styles.cancelButton, styles.wide)}
            color="primary"
            onClick={onCancel}
          >
            Cancel
          </Button>

          <Button
            size="small"
            className={cx(styles.button, styles.wide)}
            variant="contained"
            color="primary"
            onClick={handleSubmit}
          >
            Save
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default EditCustomCategoriesBookingPopup;
